import React, { useMemo } from "react";
import { getTrainingLogSignatureCell } from "../../../util/trainingLogSignatureDisplay";
import {
  buildInheritedFullyTrainedTraineeIds,
  buildTrainingLogDisplayRows,
  derivePerStepTrainingSetsFromLog,
  findLatestReleasedProcedureRevision,
  findRevisionDataEntryForTrainingLogRow,
  resolveRevisionRevLabelFromContext,
  getProcedureRevisionLookupKeyFromRow,
  isRevisionWorkflowActorForTrainingRow,
  isTraineeInInheritedFullyTrainedSet,
  normalizeProcedureRevisionIdKey,
  normalizeTraineeIdFromTrainingLogRow,
  pickReleasedDateFromRevision,
  resolveTrainingStatusLabelFromLog,
  unwrapTrainedToPayload,
} from "../../../util/trainingLogStatus";

const SIGNATURE_TEXT_STYLE = {
  fontFamily:
    "'Segoe Script', 'Brush Script MT', 'Lucida Handwriting', 'Apple Chancery', cursive",
  fontSize: 12,
  color: "#8f9092",
  fontWeight: 400,
  lineHeight: 1.2,
};

/** Compact signatures so table rows stay short. */
const SIGNATURE_IMG_STYLE = {
  maxHeight: 32,
  maxWidth: 96,
  objectFit: "contain",
  display: "block",
};

function renderSignatureCell(sig) {
  const cell = getTrainingLogSignatureCell(sig);
  if (cell.type === "img") {
    return (
      <img
        src={cell.src}
        alt=""
        className="signature"
        style={SIGNATURE_IMG_STYLE}
      />
    );
  }
  if (cell.type === "text") {
    return <span style={SIGNATURE_TEXT_STYLE}>{cell.text}</span>;
  }
  return "No Signature";
}

const formatTrainerName = (row) => {
  if (row.trainer_name && String(row.trainer_name).trim()) {
    return row.trainer_name.trim();
  }
  if (row.trainer_first_name != null || row.trainer_last_name != null) {
    const n = `${row.trainer_first_name || ""} ${row.trainer_last_name || ""}`.trim();
    if (n) return n;
  }
  const t = row.trainer;
  if (t && typeof t === "object") {
    const name = `${t.first_name || ""} ${t.last_name || ""}`.trim();
    return name || "N/A";
  }
  if (typeof t === "string" && String(t).trim()) {
    return String(t).trim();
  }
  return "N/A";
};

const formatTraineeName = (row) => {
  if (row.trainee_name && String(row.trainee_name).trim()) {
    return row.trainee_name.trim();
  }
  if (row.trainee_first_name != null || row.trainee_last_name != null) {
    const n = `${row.trainee_first_name || ""} ${row.trainee_last_name || ""}`.trim();
    if (n) return n;
  }
  const t = row.trainee;
  if (t && typeof t === "object") {
    const name = `${t.first_name || ""} ${t.last_name || ""}`.trim();
    return name || "N/A";
  }
  if (typeof t === "string" && String(t).trim()) {
    return String(t).trim();
  }
  return "N/A";
};

const formatRevisionTrained = (row) => {
  if (
    row.revision_trained != null &&
    String(row.revision_trained).trim() !== ""
  ) {
    return String(row.revision_trained).trim();
  }
  if (row.rev != null && String(row.rev).trim() !== "") {
    return String(row.rev).trim();
  }
  if (row.version_id_ref != null && String(row.version_id_ref).trim() !== "") {
    return String(row.version_id_ref);
  }
  return "N/A";
};

/**
 * Global step indices for "Step 1 & 3" labels. Delegates to `derivePerStepTrainingSetsFromLog` so
 * resolution matches Document checkmarks (wrapper `trained_to.tree_node_id`, `procedure_tree_ref`,
 * section-aware sibling_order → global index). The previous local map omitted `procedure_tree_ref` and
 * those fallbacks, so lookups failed and `parseStepNumbersFromTrainedTo` used section-local
 * `sibling_order` (often 1) instead of the real global step number.
 */
const deriveTrainedStepNumbersForRow = (row, allNodesData) => {
  const trainedTo = unwrapTrainedToPayload(row?.trained_to);
  const steps = Array.isArray(trainedTo?.steps) ? trainedTo.steps : [];
  if (!row || typeof row !== "object" || !steps.length) return null;
  if (!Array.isArray(allNodesData) || allNodesData.length === 0) return null;

  const { fullyTrained, partial, allStepsTrained } =
    derivePerStepTrainingSetsFromLog(row, allNodesData);
  if (allStepsTrained) return null;

  const nums = new Set([...fullyTrained, ...partial]);
  if (nums.size === 0) return [];
  return Array.from(nums).sort((a, b) => a - b);
};

const parseStepNumbersFromTrainedTo = (trainedTo) => {
  const inner = unwrapTrainedToPayload(trainedTo) ?? trainedTo;
  if (!inner || typeof inner !== "object") return [];
  const steps = Array.isArray(inner.steps) ? inner.steps : [];
  const nums = [];
  for (const s of steps) {
    if (!s || typeof s !== "object") continue;
    // Only count steps the trainee has completed training for.
    const isComplete = s?.is_complete === true;
    const st = String(s.status ?? "").trim().toLowerCase();
    if (
      !isComplete &&
      st !== "trained" &&
      st !== "partially trained"
    ) {
      continue;
    }

    const raw =
      s?.sibling_order ??
      s?.detail?.sibling_order ??
      s?.detail?.step_number ??
      s?.detail?.step_no;
    const n = Number.parseInt(raw, 10);
    if (Number.isFinite(n) && n > 0) nums.push(n);
  }
  // Unique + sort ascending (no renumbering; prefer `allNodesData` mapping when provided).
  return Array.from(new Set(nums)).sort((a, b) => a - b);
};

/** e.g. "Step 1", "Step 1 & 2", "Step 1, 2 & 3" (Partially Trained → completed step numbers). */
const formatStepListLabel = (numbers) => {
  const nums = Array.isArray(numbers) ? numbers.filter(Boolean) : [];
  if (nums.length === 0) return "";
  if (nums.length === 1) return `Step ${nums[0]}`;
  if (nums.length === 2) return `Step ${nums[0]} & ${nums[1]}`;
  return `Step ${nums.slice(0, -1).join(", ")} & ${nums[nums.length - 1]}`;
};

const shouldApplyInheritedTrained = (baseLabel) => {
  const s = String(baseLabel).trim().toLowerCase();
  if (s === "trained") return false;
  if (s === "partially trained") return false;
  if (/\bstep\b/i.test(s)) return false;
  if (s === "loading…" || s === "loading...") return false;
  if (s === "maintained outside system") return false;
  return true;
};

/** Same labels as Training Log / Add Module: Trained, Partially Trained, Step 1 & 2, Not Trained, etc. */
const computeBaseTrainingStatusOfTrainee = (row, allNodesData) => {
  if (!row || typeof row !== "object") return "—";

  // Prefer `trained_to` / `is_complete` (Submitter, Approver, Releaser workflow rows) over
  // `training_status_of_trainee` — GET may expose stale strings that hide API "Trained".
  const fromLog = resolveTrainingStatusLabelFromLog(row);
  if (typeof fromLog === "string" && fromLog.trim()) {
    const label = fromLog.trim();
    if (label !== "Not Trained") {
      if (/\bStep\b/i.test(label)) {
        const nums = deriveTrainedStepNumbersForRow(row, allNodesData);
        const computed = nums ? formatStepListLabel(nums) : "";
        return computed || label;
      }
      if (label === "Partially Trained") {
        let numbers = [];
        if (Array.isArray(allNodesData) && allNodesData.length > 0) {
          numbers = deriveTrainedStepNumbersForRow(row, allNodesData) || [];
        }
        let stepsLabel = formatStepListLabel(numbers);
        if (!stepsLabel) {
          const fromPayload = parseStepNumbersFromTrainedTo(row?.trained_to);
          stepsLabel = formatStepListLabel(fromPayload);
        }
        if (stepsLabel) return stepsLabel;
        return "Partially Trained";
      }
      return label;
    }
  }

  const directKeys = [
    "training_status_of_trainee",
    "training_status",
    "viewer_training_status",
    "overall_training_status",
    "status",
    "trainingStatus",
  ];
  for (const k of directKeys) {
    const v = row[k];
    if (typeof v === "string" && v.trim()) {
      const trimmed = v.trim();
      if (trimmed.toLowerCase() === "partially trained") {
        if (Array.isArray(allNodesData) && allNodesData.length > 0) {
          let numbers = deriveTrainedStepNumbersForRow(row, allNodesData) || [];
          let stepsLabel = formatStepListLabel(numbers);
          if (!stepsLabel) {
            const fromPayload = parseStepNumbersFromTrainedTo(row?.trained_to);
            stepsLabel = formatStepListLabel(fromPayload);
          }
          if (stepsLabel) return stepsLabel;
        }
        return trimmed;
      }
      return trimmed;
    }
  }
  if (row.is_maintained_outside_system) {
    return "Maintained Outside System";
  }
  return "—";
};

const formatTrainingStatusOfTrainee = (row, allNodesData, statusOptions) => {
  const {
    procedureRevisionId,
    inheritedFullyTrainedTraineeIds,
    revisionData,
  } = statusOptions || {};

  const revEntry = findRevisionDataEntryForTrainingLogRow(row, revisionData);
  if (revEntry && isRevisionWorkflowActorForTrainingRow(revEntry, row)) {
    return "Trained";
  }

  const base = computeBaseTrainingStatusOfTrainee(row, allNodesData);
  if (
    !procedureRevisionId ||
    !inheritedFullyTrainedTraineeIds ||
    inheritedFullyTrainedTraineeIds.size === 0
  ) {
    return base;
  }
  const rowRev = normalizeProcedureRevisionIdKey(
    row?.procedure_revision_ref ??
      row?.procedureRevisionId ??
      row?.procedure_revision_id ??
      "",
  );
  const curRev = normalizeProcedureRevisionIdKey(procedureRevisionId);
  if (!rowRev || rowRev !== curRev) return base;
  if (!isTraineeInInheritedFullyTrainedSet(row, inheritedFullyTrainedTraineeIds))
    return base;
  if (!shouldApplyInheritedTrained(base)) return base;
  return "Trained";
};

const trainingStatusCellStyle = {
  color: "#212529",
  textAlign: "left",
  fontWeight: 400,
};

const formatTrainingDate = (value) => {
  if (value == null || value === "") return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
};

/**
 * Submitter / Approver / Releaser: same name as Trainee when API omits trainer.
 * Same-letter inheritance (e.g. A1 SAR → Trained on A2): trainee is not on A2 `required_by` but
 * should still show a name in Trainer — use Trainee display name.
 */
const formatTrainerNameForRow = (row, revisionData, trainerOptions) => {
  const direct = formatTrainerName(row);
  if (direct && direct !== "N/A") return direct;
  const revEntry = findRevisionDataEntryForTrainingLogRow(row, revisionData);
  if (revEntry && isRevisionWorkflowActorForTrainingRow(revEntry, row)) {
    const tn = formatTraineeName(row);
    if (tn && tn !== "N/A") return tn;
  }
  const { procedureRevisionId, inheritedFullyTrainedTraineeIds } =
    trainerOptions || {};
  if (procedureRevisionId && inheritedFullyTrainedTraineeIds?.size > 0) {
    const rowRev = normalizeProcedureRevisionIdKey(
      row?.procedure_revision_ref ??
        row?.procedureRevisionId ??
        row?.procedure_revision_id ??
        "",
    );
    const curRev = normalizeProcedureRevisionIdKey(procedureRevisionId);
    if (
      rowRev &&
      rowRev === curRev &&
      isTraineeInInheritedFullyTrainedSet(row, inheritedFullyTrainedTraineeIds)
    ) {
      const tn = formatTraineeName(row);
      if (tn && tn !== "N/A") return tn;
    }
  }
  return direct;
};

/** Use revision release date when `date_of_training` is missing (workflow rows). */
const formatTrainingDateForRow = (row, revisionData) => {
  const raw = row.date_of_training;
  if (raw != null && String(raw).trim() !== "") {
    const formatted = formatTrainingDate(raw);
    if (formatted !== "—") return formatted;
  }
  const revEntry = findRevisionDataEntryForTrainingLogRow(row, revisionData);
  const fallback = pickReleasedDateFromRevision(revEntry);
  return fallback ? formatTrainingDate(fallback) : "—";
};

const TrainingLogTable = ({
  trainingLogs = [],
  showHeading = true,
  headingRightContent = null,
  tableMarginTop = null,
  tableMarginBottom = null,
  allNodesData = null,
  procedureRevisionId = null,
  currentRev = null,
  revisionData = null,
}) => {
  const letterContextRev = useMemo(
    () =>
      resolveRevisionRevLabelFromContext(
        revisionData,
        procedureRevisionId,
        currentRev,
      ),
    [revisionData, procedureRevisionId, currentRev],
  );

  const effectiveReleased = useMemo(
    () => findLatestReleasedProcedureRevision(revisionData, letterContextRev),
    [revisionData, letterContextRev],
  );
  const effectiveProcedureRevisionId =
    effectiveReleased?.procedureRevisionId ?? procedureRevisionId;
  const effectiveCurrentRev =
    effectiveReleased?.rev ?? currentRev;

  const rowsForTable = useMemo(
    () =>
      buildTrainingLogDisplayRows({
        trainingLogs,
        revisionData,
        procedureRevisionId,
        currentRev,
      }),
    [trainingLogs, revisionData, procedureRevisionId, currentRev],
  );

  const inheritedFullyTrainedTraineeIds = useMemo(
    () =>
      buildInheritedFullyTrainedTraineeIds({
        trainingLogs: rowsForTable,
        revisionData,
        currentProcedureRevisionId: effectiveProcedureRevisionId,
        currentRev: effectiveCurrentRev,
      }),
    [rowsForTable, revisionData, effectiveProcedureRevisionId, effectiveCurrentRev],
  );

  const statusOptions = useMemo(
    () => ({
      procedureRevisionId: effectiveProcedureRevisionId,
      inheritedFullyTrainedTraineeIds,
      revisionData,
    }),
    [effectiveProcedureRevisionId, inheritedFullyTrainedTraineeIds, revisionData],
  );

  const trainerOptions = useMemo(
    () => ({
      procedureRevisionId: effectiveProcedureRevisionId,
      inheritedFullyTrainedTraineeIds,
    }),
    [effectiveProcedureRevisionId, inheritedFullyTrainedTraineeIds],
  );

  return (
    <>
      {showHeading && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h3>Training Log</h3>
          {headingRightContent}
        </div>
      )}
      <div style={{ overflowX: "auto", overflowY: "visible" }}>
        <table
          className="table"
          style={{
            marginTop: tableMarginTop ?? (showHeading ? "-12px" : "14px"),
            marginBottom: tableMarginBottom ?? undefined,
          }}
        >
          <thead style={{ height: "50px" }}>
            <tr>
              <th style={{ width: "150px" }}>Date of Training</th>
              <th>Trainer</th>
              <th>Trainer Signature</th>
              <th>Trainee</th>
              <th>Trainee Signature</th>
              <th>Revision Trained</th>
              <th>Training Status of Trainee</th>
            </tr>
          </thead>
          <tbody>
            {rowsForTable.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ color: "#6c757d", padding: "12px" }}>
                  No training records for this procedure.
                </td>
              </tr>
            ) : (
              rowsForTable.map((row, index) => {
                const key = `${getProcedureRevisionLookupKeyFromRow(row)}|${normalizeTraineeIdFromTrainingLogRow(row)}|${row.training_log_id || row.id || index}`;

                return (
                  <tr key={key}>
                    <td>{formatTrainingDateForRow(row, revisionData)}</td>
                    <td>{formatTrainerNameForRow(row, revisionData, trainerOptions)}</td>
                    <td>{renderSignatureCell(row.trainer_signature)}</td>
                    <td>{formatTraineeName(row)}</td>
                    <td>{renderSignatureCell(row.trainee_signature)}</td>
                    <td>{formatRevisionTrained(row)}</td>
                    <td style={trainingStatusCellStyle}>
                      {formatTrainingStatusOfTrainee(
                        row,
                        allNodesData,
                        statusOptions,
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </>
  );
};

const escapeHtmlForPdfCell = (v) =>
  String(v ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const escapeHtmlAttrForPdf = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

/**
 * Training log rows for PDF export — same data as the Document / Preview Training Log table
 * (`buildTrainingLogDisplayRows` + the same per-cell formatters as `TrainingLogTable`).
 */
export function computeTrainingLogExportRowsForPdf({
  trainingLogs = [],
  revisionData = null,
  procedureRevisionId = null,
  currentRev = null,
  allNodesData = null,
}) {
  const letterContextRev = resolveRevisionRevLabelFromContext(
    revisionData,
    procedureRevisionId,
    currentRev,
  );
  const effectiveReleased = findLatestReleasedProcedureRevision(
    revisionData,
    letterContextRev,
  );
  const effectiveProcedureRevisionId =
    effectiveReleased?.procedureRevisionId ?? procedureRevisionId;
  const effectiveCurrentRev = effectiveReleased?.rev ?? currentRev;

  const rowsForTable = buildTrainingLogDisplayRows({
    trainingLogs,
    revisionData,
    procedureRevisionId,
    currentRev,
  });

  const inheritedFullyTrainedTraineeIds = buildInheritedFullyTrainedTraineeIds({
    trainingLogs: rowsForTable,
    revisionData,
    currentProcedureRevisionId: effectiveProcedureRevisionId,
    currentRev: effectiveCurrentRev,
  });

  const statusOptions = {
    procedureRevisionId: effectiveProcedureRevisionId,
    inheritedFullyTrainedTraineeIds,
    revisionData,
  };

  const trainerOptions = {
    procedureRevisionId: effectiveProcedureRevisionId,
    inheritedFullyTrainedTraineeIds,
  };

  const signatureCellHtml = (sig) => {
    const cell = getTrainingLogSignatureCell(sig);
    if (cell.type === "img") {
      return `<img src="${escapeHtmlAttrForPdf(cell.src)}" alt="" class="signature" style="max-height:32px;max-width:96px;object-fit:contain;display:block;" />`;
    }
    if (cell.type === "text") {
      return `<span style="font-family:'Segoe Script','Brush Script MT',cursive;font-size:12px;color:#8f9092;">${escapeHtmlForPdfCell(cell.text)}</span>`;
    }
    return "No Signature";
  };

  if (rowsForTable.length === 0) {
    return {
      rows: [],
      emptyMessage: "No training records for this procedure.",
      displaySourceRows: [],
    };
  }

  const rows = rowsForTable.map((row) => ({
    date: escapeHtmlForPdfCell(formatTrainingDateForRow(row, revisionData)),
    trainer: escapeHtmlForPdfCell(
      formatTrainerNameForRow(row, revisionData, trainerOptions),
    ),
    trainerSigHtml: signatureCellHtml(row.trainer_signature),
    trainee: escapeHtmlForPdfCell(formatTraineeName(row)),
    traineeSigHtml: signatureCellHtml(row.trainee_signature),
    revisionTrained: escapeHtmlForPdfCell(formatRevisionTrained(row)),
    status: escapeHtmlForPdfCell(
      formatTrainingStatusOfTrainee(row, allNodesData, statusOptions),
    ),
  }));

  /** Same objects as Document `TrainingLogTable` tbody (signatures, workflow rows, synthetics). */
  return { rows, emptyMessage: null, displaySourceRows: rowsForTable };
}

/**
 * jsPDF autoTable row matrix + source rows for didDrawCell signatures — matches Document table row count
 * and per-cell text (not raw API `trainingLogsData.length`).
 */
export function computeTrainingLogAutotableRowsForPdf({
  trainingLogs = [],
  revisionData = null,
  procedureRevisionId = null,
  currentRev = null,
  allNodesData = null,
}) {
  const letterContextRev = resolveRevisionRevLabelFromContext(
    revisionData,
    procedureRevisionId,
    currentRev,
  );
  const effectiveReleased = findLatestReleasedProcedureRevision(
    revisionData,
    letterContextRev,
  );
  const effectiveProcedureRevisionId =
    effectiveReleased?.procedureRevisionId ?? procedureRevisionId;
  const effectiveCurrentRev = effectiveReleased?.rev ?? currentRev;

  const rowsForTable = buildTrainingLogDisplayRows({
    trainingLogs,
    revisionData,
    procedureRevisionId,
    currentRev,
  });

  const inheritedFullyTrainedTraineeIds = buildInheritedFullyTrainedTraineeIds({
    trainingLogs: rowsForTable,
    revisionData,
    currentProcedureRevisionId: effectiveProcedureRevisionId,
    currentRev: effectiveCurrentRev,
  });

  const statusOptions = {
    procedureRevisionId: effectiveProcedureRevisionId,
    inheritedFullyTrainedTraineeIds,
    revisionData,
  };

  const trainerOptions = {
    procedureRevisionId: effectiveProcedureRevisionId,
    inheritedFullyTrainedTraineeIds,
  };

  const tableMatrix = rowsForTable.map((row) => [
    formatTrainingDateForRow(row, revisionData),
    formatTrainerNameForRow(row, revisionData, trainerOptions),
    "",
    formatTraineeName(row),
    "",
    formatRevisionTrained(row),
    formatTrainingStatusOfTrainee(row, allNodesData, statusOptions),
  ]);

  return { tableMatrix, displaySourceRows: rowsForTable };
}

export default TrainingLogTable;
